import axiosInstance from '../../Components/Request';

/**
 * NOTIFICACIONES — cliente HTTP.
 *
 * Archivo propio y no dentro de `prealert/api.ts`, que es donde acabó la auditoría: las notificaciones no
 * son de pre-alerta y este módulo va a crecer (bandeja, preferencias, suscripciones, y más adelante el
 * interruptor de administración). Meterlo ahí solo haría más grande un archivo que ya es una bolsa.
 */

export type NotificationChannel = 'platform' | 'email';
export type ShipmentKind = 'ocean' | 'air' | 'load';

/** Definición de un tipo de aviso. **Viene del backend**, no se escribe aquí. */
export interface NotificationEventDef {
  type: string;
  label: string;
  description: string;
  defaults: Record<NotificationChannel, boolean>;
  /** `true` = solo tiene sentido para quien supervisa. Se usa para agrupar, no para esconder. */
  supervisory?: boolean;
}

export interface ResolvedPreference {
  eventType: string;
  channel: NotificationChannel;
  enabled: boolean;
  /** `true` cuando el usuario lo cambió a mano, en lugar de heredar el default. */
  overridden: boolean;
}

export interface NotificationSettings {
  /**
   * 🔑 El catálogo llega del servidor junto con las preferencias, así que la pantalla **no tiene una lista
   * de eventos escrita a mano**. Agregar un aviso es tocar un archivo del backend y la casilla aparece sola.
   */
  events: NotificationEventDef[];
  preferences: ResolvedPreference[];
  /**
   * ⚠️ `false` = al usuario no le puede llegar un correo (sin dirección, o sin verificar), así que el canal de
   * correo **no va a entregar nada**. Sin este dato, alguien la prende, no recibe nada y no hay forma de saber por
   * qué.
   *
   * 🔄 Desde el 29-sep la pantalla **bloquea** la columna de correo cuando es `false`, y el backend rechaza el
   * guardado que intente prenderla. Lo calcula con la misma regla que decide si un correo sale.
   */
  emailReady: boolean;
  email: string | null;
  /**
   * A qué horas le llegan sus avisos (09-oct-2026). **Opcional** porque un backend anterior no lo manda: la sección
   * se esconde en lugar de ofrecer algo que no se puede guardar.
   */
  schedule?: DeliverySchedule;
}

/**
 * Las horas en que a una persona le llegan sus avisos.
 *
 * 🔑 Se pidió el 09-oct: *"debería ser por usuario... de manera general, no de carga por carga"*. Con horas, lo que
 * pasa en medio espera y llega junto a la siguiente hora: en la campanita un renglón por aviso, y por correo uno solo.
 */
export interface DeliverySchedule {
  /** `false` = falta el SQL de la tabla en este servidor. La sección lo dice y no deja guardar. */
  available: boolean;
  /**
   * `false` = el reloj que entrega lo retenido no corre en este servidor, así que todo sigue llegando cuando pasa
   * aunque haya horas guardadas. La sección lo dice para que nadie crea que sus horas están funcionando.
   */
  enforced: boolean;
  /** `HH:MM` separadas por coma, o `null` = cuando pasan. */
  sendHours: string | null;
  timeZone: string;
}

export const getNotificationSettings = async (): Promise<NotificationSettings> => {
  const { data } = await axiosInstance.get('/me/notification-preferences');
  return data;
};

/** Guarda las horas propias. `sendHours: null` = cuando pasan. */
export const saveNotificationSchedule = async (
  sendHours: string | null,
  timezone: string
): Promise<DeliverySchedule> => {
  const { data } = await axiosInstance.put('/me/notification-schedule', { sendHours, timezone });
  return data.schedule;
};

/**
 * Las horas de **otra** persona, desde su detalle. Ruta distinta a la de arriba por la misma razón que las
 * preferencias: ver la nota de `getUserNotificationSettings`.
 */
export const saveUserNotificationSchedule = async (
  userId: string,
  sendHours: string | null,
  timezone: string
): Promise<DeliverySchedule> => {
  const { data } = await axiosInstance.put(`/users/${userId}/notification-schedule`, { sendHours, timezone });
  return data.schedule;
};

export const saveNotificationSettings = async (
  preferences: Array<{ eventType: string; channel: NotificationChannel; enabled: boolean }>
): Promise<NotificationSettings['preferences']> => {
  const { data } = await axiosInstance.put('/me/notification-preferences', { preferences });
  return data.preferences || [];
};

/* -------------------------------------------------------------------------- */
/*            Preferencias de OTRO usuario — vista de administrador           */
/* -------------------------------------------------------------------------- */

/**
 * 🔴 **Rutas distintas a propósito, no un parámetro opcional sobre las de arriba.**
 *
 * El backend las separó por la misma razón (ver `userNotificationController`): una función que apunte a `/me`
 * *si no* le pasan id y a `/users/:id` *si sí* es una función donde un `undefined` inesperado hace que un
 * administrador edite sus propias preferencias creyendo que edita las de otra persona. Con dos funciones, el
 * error de programación no existe: la que no recibe id **no puede** tocar a nadie más.
 *
 * 📌 `user` viene en la respuesta para que la pantalla pueda decir de **quién** son las preferencias que
 * muestra. Sin ese dato, el formulario es idéntico al de "Mi perfil" y se presta a la confusión que el párrafo
 * de arriba describe.
 */
export const getUserNotificationSettings = async (
  userId: string
): Promise<NotificationSettings & { user: { id: string; name: string; email: string } }> => {
  const { data } = await axiosInstance.get(`/users/${userId}/notification-preferences`);
  return data;
};

export const saveUserNotificationSettings = async (
  userId: string,
  preferences: Array<{ eventType: string; channel: NotificationChannel; enabled: boolean }>
): Promise<NotificationSettings['preferences']> => {
  const { data } = await axiosInstance.put(`/users/${userId}/notification-preferences`, {
    preferences,
  });
  return data.preferences || [];
};

/* -------------------------------------------------------------------------- */
/*                                  Bandeja                                   */
/* -------------------------------------------------------------------------- */

/**
 * Forma de `notification.data`.
 *
 * 📌 `kind` discrimina: hoy solo existe `job_run`, pero las **advertencias** y el **retraso por tramo** de la
 * integración con SupplyMaven van a usar la misma columna. Sin el discriminador, la pantalla tendría que adivinar
 * qué trae el objeto mirando qué llaves existen — que es cómo se acaba dibujando una lista de advertencias como si
 * fueran embarques.
 */
export interface JobRunData {
  kind: 'job_run';
  counts: { scanned: number; moved: number; skipped: number; failed: number };
  items: Array<{ reference: string; status: string }>;
  /** Cuántos quedaron fuera de `items`. Se dice en lugar de callarlo. */
  more: number;
}

/** ⚠️ Unión abierta a propósito: un `kind` desconocido no debe romper la bandeja, solo no dibujarse. */
export type NotificationData = JobRunData | { kind: string; [key: string]: unknown };

export interface NotificationItem {
  id: string;
  eventType: string;
  title: string;
  body: string | null;
  shipmentId: string | null;
  shipmentType: ShipmentKind | null;
  /**
   * Folio, estatus actual y estatus anterior.
   *
   * ⚠️ **Opcionales porque las columnas todavía no existen.** Salen de `SQL_NOTIFICATION_VISUALS.sql`, que
   * está escrito y pendiente de correr; hasta entonces el backend no los manda y la interfaz **esconde** las
   * insignias en lugar de inventarlas.
   *
   * 🔑 Se declaran ya para que la pantalla se escriba una sola vez. Y son opcionales de forma permanente, no
   * temporal: un aviso puede no tener estatus —el resumen del JOB, el de prueba— y los avisos que ya existen
   * se quedan sin ellos para siempre.
   */
  reference?: string | null;
  status?: string | null;
  statusBefore?: string | null;
  /**
   * Datos estructurados, para los avisos que llevan una **lista** en lugar de una frase.
   *
   * 🔑 Hoy lo usa el resumen de la corrida del JOB: sus conteos se pintan como insignias y lo que se movió como
   * mini tarjetas, en lugar de apretarse en un párrafo donde hay que llegar al final para saber si algo falló.
   *
   * ⚠️ Opcional y **nulo en la mayoría**: un cambio de estatus no necesita lista, ya tiene folio y estatus en sus
   * propios campos. Y los avisos anteriores a `SQL_NOTIFICATION_DATA.sql` se quedan sin él para siempre.
   */
  data?: NotificationData | null;
  /** De dónde salió: preferencia general o suscripción explícita a ese embarque. */
  source: 'preference' | 'subscription';
  read: boolean;
  readAt: string | null;
  createdAt: string;
}

/**
 * A dónde lleva un aviso.
 *
 * 🔑 Vive aquí y no en cada pantalla porque **lo usan dos**: la campanita y la página de la bandeja. Con una
 * copia en cada una, el día que se agregue una modalidad o cambie una ruta una de las dos se queda atrás — y
 * el síntoma sería un aviso que lleva a la pantalla equivocada, que nadie reporta como un error de código.
 *
 * 📌 Devuelve `null` cuando el aviso no apunta a ningún embarque (el resumen del JOB, o el de prueba). Quien
 * llama decide qué hacer con eso; aquí no se inventa un destino.
 */
export const notificationLink = (n: NotificationItem): string | null => {
  if (!n.shipmentId) return null;
  if (n.shipmentType === 'load') return `/guide/detail/${n.shipmentId}`;
  return `/shipments/${n.shipmentId}`;
};

export const getNotifications = async (
  opts: {
    unread?: boolean;
    limit?: number;
    offset?: number;
    types?: string[];
    /**
     * Pide el total y el desglose por tipo **aunque no haya `offset`**, que es el caso de la primera página.
     *
     * 🔴 **La bandeja ya lo mandaba y aquí se tiraba** (corregido el 07-oct-2026). En la primera página `offset` es 0
     * y no viaja, así que el backend no calculaba ni el total ni el desglose: la bandeja se quedaba sin paginador y
     * sin las pastillas de tipo, y no había forma de pasar de los primeros 25 avisos. El backend acepta `total=true`
     * desde siempre; faltaba este lado.
     */
    total?: boolean;
  } = {}
): Promise<{
  items: NotificationItem[];
  unread: number;
  total: number | null;
  /**
   * Cuántos hay de cada tipo, para las pastillas del filtro.
   *
   * 🔑 **Llega sin aplicar el filtro de tipos**, a propósito: las pastillas son el selector, y si se
   * descontaran, al elegir una las demás caerían a cero y no se podría cambiar de selección. Sí respeta el
   * filtro de "no leídos".
   *
   * 📌 `null` cuando no se pidió página — la campanita no lo necesita y no debe pagar el `GROUP BY`.
   */
  byType: Record<string, number> | null;
}> => {
  const { data } = await axiosInstance.get('/me/notifications', {
    params: {
      ...(opts.unread ? { unread: 'true' } : {}),
      ...(opts.limit ? { limit: opts.limit } : {}),
      /**
       * 📌 `offset` se manda **solo cuando se pagina**. El backend usa su presencia para decidir si calcula
       * el total, y la campanita —que llama cada minuto, en cada pantalla y por usuario— no lo necesita.
       */
      ...(opts.offset ? { offset: opts.offset } : {}),
      /** 📌 Solo la bandeja lo pide. La campanita no lo manda y sigue sin pagar el `COUNT`. */
      ...(opts.total ? { total: 'true' } : {}),
      /** Lista separada por comas. El backend la intersecta con su catálogo. */
      ...(opts.types && opts.types.length ? { types: opts.types.join(',') } : {}),
    },
  });
  return {
    items: data.items || [],
    unread: data.unread || 0,
    /** `null` cuando no se pidió: distinto de `0`, que significaría "no hay ninguno". */
    total: typeof data.total === 'number' ? data.total : null,
    byType: data.byType && typeof data.byType === 'object' ? data.byType : null,
  };
};

/**
 * Se manda un aviso de prueba **a la propia bandeja**.
 *
 * 🔑 Es lo que permite distinguir "la bandeja está rota" de "todavía no ha pasado nada", que sin esto son
 * indistinguibles: el módulo se estrena vacío y para que salga un aviso real tiene que moverse un embarque.
 *
 * ⚠️ **No manda correo.** Escribe en la bandeja y nada más.
 */
export const sendTestNotification = async (): Promise<number> => {
  const { data } = await axiosInstance.post('/me/notifications/test', {});
  return data.unread || 0;
};

/** Sin `ids` marca **todo** como leído, que es la acción más usada de cualquier bandeja. */
export const markNotificationsRead = async (
  ids?: string[]
): Promise<{ updated: number; unread: number }> => {
  const { data } = await axiosInstance.post('/me/notifications/read', ids ? { ids } : {});
  return { updated: data.updated || 0, unread: data.unread || 0 };
};

/* -------------------------------------------------------------------------- */
/*                              Suscripciones                                 */
/* -------------------------------------------------------------------------- */

export const getSubscription = async (shipmentId: string): Promise<string[]> => {
  const { data } = await axiosInstance.get(`/me/subscriptions/${shipmentId}`);
  return data.events || [];
};

/**
 * Reemplaza la suscripción. **Mandar una lista vacía deja de seguir el embarque.**
 *
 * 📌 Es reemplazo y no acumulación: si solo agregara, desmarcar una casilla no haría nada y la persona
 * seguiría recibiendo lo que quitó.
 */
export const setSubscription = async (
  shipmentId: string,
  shipmentType: ShipmentKind,
  events: string[]
): Promise<string[]> => {
  const { data } = await axiosInstance.put(`/me/subscriptions/${shipmentId}`, {
    shipmentType,
    events,
  });
  return data.events || [];
};
